import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Map, MapPin, Loader2, X, AlertTriangle, Crosshair, Info, ImageOff } from 'lucide-react';
import Evidence360Modal from './Evidence360Modal';
import SiteWalkTimeTravel from '../SiteWalkTimeTravel';
import { P } from '../../lib/sitewalkApi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Floor-Plan Canvas. Reuses the existing Drawing infrastructure (design/drawings + reference-drawings/file).
 * Shows Capture Location markers on a SPECIFIC drawing revision, supports placing a location
 * (Floor-Plan Positioned, NOT geometrically registered), Plan->Panorama, and revision-retention warnings.
 */
export default function FloorPlanCanvas({ projectId, focusCapturePoint = null, onClose = null }) {
  const [drawings, setDrawings] = useState([]);
  const [drawingId, setDrawingId] = useState('');
  const [imgUrl, setImgUrl] = useState(null);
  const [imgError, setImgError] = useState(false);
  const [walks, setWalks] = useState([]);
  const [walkId, setWalkId] = useState('');
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [placeCp, setPlaceCp] = useState('');       // capture point id being placed
  const [evidence, setEvidence] = useState(null);
  const [history, setHistory] = useState(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const d = (await axios.get(`${P(projectId)}/design/drawings`, { params: { pageSize: 200 } })).data;
        const items = d.items || d.drawings || [];
        setDrawings(items);
        const w = (await axios.get(`${P(projectId)}/site-walks`, { params: { pageSize: 200 } })).data;
        setWalks(w.items || []);
        if (focusCapturePoint?.floorPlanPosition?.drawingId) {
          setDrawingId(focusCapturePoint.floorPlanPosition.drawingId);
          setWalkId(focusCapturePoint.siteWalkId);
        } else if (items[0]) setDrawingId(items[0].id);
      } catch (e) { toast.error('Failed to load drawings'); }
    })();
  }, [projectId]);

  // load drawing image (graceful if no file bytes)
  useEffect(() => {
    if (!drawingId) return;
    setImgUrl(null); setImgError(false);
    let revoked = null;
    (async () => {
      try {
        const resp = await axios.get(`${API}/reference-drawings/${drawingId}/file`, { responseType: 'blob' });
        revoked = URL.createObjectURL(resp.data); setImgUrl(revoked);
      } catch (e) { setImgError(true); }
    })();
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [drawingId]);

  const loadPoints = async (wid) => {
    if (!wid) { setPoints([]); return; }
    setLoading(true);
    try { setPoints((await axios.get(`${P(projectId)}/site-walks/${wid}/capture-points`)).data.items || []); }
    catch (e) { setPoints([]); }
    setLoading(false);
  };
  useEffect(() => { loadPoints(walkId); /* eslint-disable-next-line */ }, [walkId]);

  const markers = points.filter((p) => p.floorPlanPosition?.drawingId === drawingId);
  const unplaced = points.filter((p) => !p.floorPlanPosition?.drawingId);

  const onCanvasClick = async (e) => {
    if (!placeCp) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    try {
      await axios.patch(`${P(projectId)}/capture-points/${placeCp}`, { floorPlanPosition: { drawingId, x, y } });
      toast.success('Capture location positioned on plan (Floor-Plan Positioned)');
      setPlaceCp(''); await loadPoints(walkId);
    } catch (err) { toast.error(err.response?.data?.detail || 'Positioning failed'); }
  };

  const body = (
    <div data-testid="floor-plan-canvas">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Map className="w-4 h-4 text-sky-600" />
        <select value={drawingId} onChange={(e) => setDrawingId(e.target.value)} className="border rounded-lg px-2 py-1 text-xs" data-testid="fp-drawing-select">
          <option value="">Select drawing…</option>
          {drawings.map((d) => <option key={d.id} value={d.id}>{d.drawingNumber} · rev {d.revision} {d.status === 'CURRENT' ? '(current)' : ''}</option>)}
        </select>
        <select value={walkId} onChange={(e) => setWalkId(e.target.value)} className="border rounded-lg px-2 py-1 text-xs" data-testid="fp-walk-select">
          <option value="">Select site walk…</option>
          {walks.map((w) => <option key={w.id} value={w.id}>{w.walkNumber}</option>)}
        </select>
        {walkId && (
          <>
            <select value={placeCp} onChange={(e) => setPlaceCp(e.target.value)} className="border rounded-lg px-2 py-1 text-xs" data-testid="fp-place-select">
              <option value="">Place a capture location…</option>
              {unplaced.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            {placeCp && <span className="text-[11px] text-sky-700 flex items-center gap-1" data-testid="fp-place-hint"><Crosshair className="w-3.5 h-3.5" /> click on the plan to position</span>}
          </>
        )}
      </div>

      <div ref={canvasRef} onClick={onCanvasClick}
        className={`relative w-full rounded-lg border border-slate-200 bg-slate-100 overflow-hidden ${placeCp ? 'cursor-crosshair' : ''}`}
        style={{ aspectRatio: '4 / 3' }} data-testid="fp-canvas-surface">
        {imgUrl ? (
          <img src={imgUrl} alt="floor plan" className="absolute inset-0 w-full h-full object-contain pointer-events-none" />
        ) : imgError ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 gap-1" data-testid="fp-no-image">
            <ImageOff className="w-8 h-8" /><p className="text-xs">Drawing image unavailable — markers still positioned on the grid.</p>
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        )}
        {/* markers */}
        {markers.map((p) => {
          const isFocus = focusCapturePoint && p.id === focusCapturePoint.id;
          return (
            <button key={p.id} title={p.name}
              onClick={(e) => { e.stopPropagation(); if (!placeCp) setEvidence(p); }}
              className="absolute -translate-x-1/2 -translate-y-1/2 z-10" data-testid={`fp-marker-${p.id}`}
              style={{ left: `${p.floorPlanPosition.x * 100}%`, top: `${p.floorPlanPosition.y * 100}%` }}>
              <span className={`flex items-center justify-center w-6 h-6 rounded-full border-2 border-white shadow ${isFocus ? 'bg-amber-500 ring-2 ring-amber-300' : 'bg-sky-600'}`}>
                <MapPin className="w-3.5 h-3.5 text-white" />
              </span>
              {p.newerRevisionAvailable && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-400 rounded-full border border-white" />}
            </button>
          );
        })}
      </div>

      <p className="text-[11px] text-slate-400 mt-2 flex items-center gap-1"><Info className="w-3.5 h-3.5" /> Markers are <b>Floor-Plan Positioned</b> — not geometrically registered. Positions stay pinned to their drawing revision.</p>
      {markers.some((m) => m.newerRevisionAvailable) && (
        <p className="text-[11px] text-amber-700 flex items-center gap-1 mt-1" data-testid="fp-newer-rev-warning"><AlertTriangle className="w-3.5 h-3.5" /> A newer drawing revision is available; historical positions remain on their original revision.</p>
      )}

      {/* selected marker actions */}
      {evidence && !placeCp && (
        <div className="mt-2 flex flex-wrap gap-2" data-testid="fp-marker-actions">
          <button onClick={() => setHistory(evidence)} className="text-xs px-3 py-1.5 rounded-lg border text-slate-600" data-testid="fp-history-btn">Time Travel</button>
          <span className="text-[11px] text-slate-400 self-center">Selected: {evidence.name}</span>
        </div>
      )}

      {evidence && !placeCp && <Evidence360Modal projectId={projectId} params={{ capturePointId: evidence.id }} onClose={() => setEvidence(null)} />}
      {history && <SiteWalkTimeTravel projectId={projectId} capturePoint={history} onClose={() => setHistory(null)} />}
    </div>
  );

  if (!onClose) return body;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 md:p-6" data-testid="fp-modal">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[92vh] overflow-y-auto p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold text-slate-800 flex items-center gap-2"><Map className="w-5 h-5 text-sky-600" /> Show on Plan</h3>
          <button onClick={onClose} data-testid="fp-modal-close"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        {body}
      </div>
    </div>
  );
}
